/**
 * ConnectAccountsCta — first-run state for the sidebar. Shown when no events
 * exist and we suspect no provider is connected yet. Two big buttons + a hint
 * pointing to settings for managing connections later.
 */
import { Calendar as CalendarIcon, Mail } from 'lucide-react';
import { connectGoogle, connectOffice365 } from '../../lib/calendar';

interface Props {
  userId: string;
  tenantId: string;
}

export function ConnectAccountsCta({ userId, tenantId }: Props) {
  return (
    <div className="m-3 rounded-xl border border-white/10 bg-white/[0.02] p-4">
      <div className="text-xs font-semibold text-neutral-100">Connect your calendar + email</div>
      <p className="mt-1 text-[11px] leading-relaxed text-neutral-400">
        See every meeting + filtered inbox right next to the map. Read-only —
        we never write to your calendar or send mail.
      </p>
      <div className="mt-3 grid gap-2">
        <button
          type="button"
          onClick={() => connectGoogle(userId, tenantId)}
          className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-neutral-200 transition hover:bg-white/[0.06]"
        >
          <span className="flex items-center gap-2">
            <GoogleGlyph />
            Connect Google
          </span>
          <span className="text-[10px] text-neutral-500">Calendar + Gmail</span>
        </button>
        <button
          type="button"
          onClick={() => connectOffice365(userId, tenantId)}
          className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-neutral-200 transition hover:bg-white/[0.06]"
        >
          <span className="flex items-center gap-2">
            <MicrosoftGlyph />
            Connect Microsoft 365
          </span>
          <span className="text-[10px] text-neutral-500">Outlook + Mail</span>
        </button>
      </div>
      <div className="mt-3 flex items-center gap-3 text-[10px] text-neutral-500">
        <CalendarIcon className="h-3 w-3" />
        <span>events sync every 15 min</span>
        <Mail className="h-3 w-3" />
        <span>email every 5 min</span>
      </div>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 48 48" fill="none">
      <path fill="#FFC107" d="M43.6 20.5H24v8.6h11.3c-1.6 4.6-5.9 7.9-11.3 7.9-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l6.1-6.1A19.9 19.9 0 0 0 24 4C12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 20-8 20-20 0-1.2-.1-2.3-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l7 5.1A12 12 0 0 1 24 12c3.1 0 5.9 1.2 8 3.1l6.1-6.1A19.9 19.9 0 0 0 24 4 19.9 19.9 0 0 0 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.3 0 10.1-2 13.7-5.4l-6.3-5.3a12 12 0 0 1-7.4 2.7c-5.4 0-9.7-3.2-11.3-7.7l-7 5.4A19.9 19.9 0 0 0 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H24v8.6h11.3a12 12 0 0 1-4 5.3l6.3 5.3C41.5 36.5 44 30.7 44 24c0-1.2-.1-2.3-.4-3.5z" />
    </svg>
  );
}

function MicrosoftGlyph() {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 23 23" fill="none">
      <rect width="10" height="10" x="1" y="1" fill="#F25022" />
      <rect width="10" height="10" x="12" y="1" fill="#7FBA00" />
      <rect width="10" height="10" x="1" y="12" fill="#00A4EF" />
      <rect width="10" height="10" x="12" y="12" fill="#FFB900" />
    </svg>
  );
}
